import { defaultBaseQuery, transformErrorResponse, transformSuccessResponse } from '@/services';
import { createApi } from '@reduxjs/toolkit/query/react';

const staffAttendanceAPI = createApi({
  reducerPath: 'staffAttendanceAPI',
  baseQuery: defaultBaseQuery,
  tagTypes: ['StaffAttendance', 'AttendanceHistory', 'LeaveConfiguration', 'LeaveHistory'],
  endpoints: (builder) => ({
    getStaffAttendance: builder.query({
      query: ({ fromDate, toDate, employeeCode, department } = {}) => ({
        url: `/staff/attendance/history`,
        method: 'GET',
        params: { fromDate, toDate, employeeCode, department },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ['StaffAttendance'],
    }),
    getAttendanceHistory: builder.query({
      query: ({ fromDate, toDate, page = 0, size = 10, isWeek = true, searchText = '', sortBy = '', sortDir = '' } = {}) => ({
        url: `/attendance/history`,
        method: 'GET',
        params: { fromDate, toDate, page, size, isWeek, ...(searchText && { searchText }), ...(sortBy && { sortBy }), ...(sortDir && { sortDir }) },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ['AttendanceHistory'],
    }),
    exportAttendanceHistoryExcel: builder.query({
      query: ({ fromDate, toDate, isMonth = true, sortBy = '', sortDir = '' } = {}) => ({
        url: `/attendance/history/excel`,
        method: 'GET',
        params: { fromDate, toDate, isMonth, ...(sortBy && { sortBy }), ...(sortDir && { sortDir }) },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `attendance_history_${fromDate}_${toDate}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
        cache: 'no-cache',
      }),
      transformErrorResponse: transformErrorResponse,
    }),
    getShiftByPerson: builder.query({
      query: ({ fromDate, toDate }) => ({
        url: `/shift/by-person`,
        method: 'GET',
        params: { fromDate, toDate },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ['ShiftSchedule'],
    }),
    getLeaveConfiguration: builder.query({
      query: () => ({
        url: `/leave/configuration`,
        method: 'GET',
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ['LeaveConfiguration'],
    }),
    applyLeave: builder.mutation({
      query: ({ formData, file }) => {
        const fd = new FormData();
        fd.append('formData', JSON.stringify(formData));
        if (file) fd.append('file', file);
        return { url: '/leave/apply', method: 'POST', body: fd };
      },
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ['LeaveHistory'],
    }),
    cancelLeave: builder.mutation({
      query: ({ leaveId, cancelReason }) => ({
        url: '/leave/cancel',
        method: 'PUT',
        body: { leaveId, cancelReason },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ['LeaveHistory'],
    }),
    withdrawLeave: builder.mutation({
      query: (id) => ({
        url: `/leave/withdraw/${id}`,
        method: 'PUT',
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ['LeaveHistory'],
    }),
    updateLeaveById: builder.mutation({
      query: ({ id, formData, file }) => {
        const fd = new FormData();
        fd.append('formData', JSON.stringify(formData));
        if (file) fd.append('file', file);
        return { url: `/leave/${id}`, method: 'PUT', body: fd };
      },
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      invalidatesTags: ['LeaveHistory'],
    }),
    getLeaveById: builder.query({
      query: (id) => ({
        url: `/leave/${id}`,
        method: 'GET',
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
    }),
    getLeaveHistory: builder.query({
      query: ({ fromDate, toDate, isMonth = false, page = 0, size = 10, searchText = '', sortBy = '', sortDir = '' } = {}) => ({
        url: `/leave/history`,
        method: 'GET',
        params: { fromDate, toDate, isMonth, page, size, ...(searchText && { searchText }), ...(sortBy && { sortBy }), ...(sortDir && { sortDir }) },
      }),
      transformResponse: transformSuccessResponse,
      transformErrorResponse: transformErrorResponse,
      providesTags: ['LeaveHistory'],
    }),
    exportLeaveHistoryExcel: builder.query({
      query: ({ fromDate, toDate }) => ({
        url: `/leave/history/excel`,
        method: 'GET',
        params: { fromDate, toDate },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `leave_history_${fromDate}_${toDate}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
        cache: 'no-cache',
      }),
      transformErrorResponse: transformErrorResponse,
    }),
  }),
});

export const {
  useGetStaffAttendanceQuery,
  useLazyGetStaffAttendanceQuery,
  useGetAttendanceHistoryQuery,
  useLazyGetAttendanceHistoryQuery,
  useLazyExportAttendanceHistoryExcelQuery,
  useGetShiftByPersonQuery,
  useLazyGetShiftByPersonQuery,
  useGetLeaveConfigurationQuery,
  useLazyGetLeaveConfigurationQuery,
  useCancelLeaveMutation,
  useWithdrawLeaveMutation,
  useApplyLeaveMutation,
  useUpdateLeaveByIdMutation,
  useLazyGetLeaveByIdQuery,
  useLazyGetLeaveHistoryQuery,
  useLazyExportLeaveHistoryExcelQuery,
} = staffAttendanceAPI;

export default staffAttendanceAPI;
